<template>
  <div class="app-container">
    <div class="selection-header">
      <h2>选课中心</h2>
      <div>
        <el-button type="primary" @click="viewSelectedCourses" style="margin-right: 10px">已选课程</el-button>
        <el-button type="success" @click="finishSelection">选课完成</el-button>
      </div>
    </div>

    <!-- 筛选和搜索区域 -->
    <div class="filter-section" v-show="!showSelectedCoursesTable">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input
            v-model="searchKeyword"
            placeholder="请输入课程名称进行搜索"
            prefix-icon="el-icon-search"
            clearable
            @input="handleSearch"
            @clear="handleSearch"
          />
        </el-col>
        <el-col :span="6">
          <el-input
            v-model="teacherSearchKeyword"
            placeholder="请输入教师姓名进行搜索"
            prefix-icon="el-icon-user"
            clearable
            @input="handleTeacherSearch"
            @clear="handleTeacherSearch"
          />
        </el-col>
        <el-col :span="6">
          <el-select
            v-model="selectedOrganization"
            placeholder="请选择学校/单位"
            clearable
            @change="handleFilter"
            style="width: 100%"
          >
            <el-option
              v-for="org in organizationOptions"
              :key="org"
              :label="org"
              :value="org"
            />
          </el-select>
        </el-col>
        <el-col :span="6">
          <el-button type="primary" @click="resetFilters" icon="el-icon-refresh">重置筛选</el-button>
        </el-col>
      </el-row>
    </div>

    <!-- 课程表格 -->
    <el-table :data="filteredCourseList" border style="width: 100%" :header-cell-style="{background: '#f5f7fa', color: '#606266'}"
      v-show="!showSelectedCoursesTable" v-loading="loading" row-height="100">
      <el-table-column label="课程封面" align="center" width="120">
        <template slot-scope="scope">
          <div class="image-wrapper" @click="previewImage(scope.row.image)">
            <img v-if="scope.row.image" :src="formatImageUrl(scope.row.image)" class="course-cover" alt="课程封面" @error="handleImageError($event, scope.row)">
            <div v-else class="no-image">暂无封面</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="course_name" label="课程名称" min-width="120" align="center" />
      <el-table-column prop="teacher_name" label="教师姓名" min-width="100" align="center" />
      <el-table-column prop="organization" label="所属单位" min-width="120" align="center" />
      <el-table-column prop="term" label="学期" min-width="100" align="center" />
      <el-table-column prop="profile" label="课程简介" min-width="180" align="center" />
      <el-table-column prop="target" label="课程目标" min-width="180" align="center" />
      <el-table-column label="操作" align="center" width="180" fixed="right">
        <template slot-scope="scope">
          <el-button
            type="primary"
            size="small"
            @click="selectCourse(scope.row)"
            :disabled="isSelected(scope.row)"
            :loading="selectLoading"
            style="margin-right: 5px"
          >
            {{ isSelected(scope.row) ? '已选' : '选课' }}
          </el-button>
          <el-button
            v-if="isSelected(scope.row)"
            type="info"
            size="small"
            @click="viewCourseDetail(scope.row)"
          >
            查看详情
          </el-button>
          <el-button
            v-else
            type="info"
            size="small"
            @click="viewCourseBasicInfo(scope.row)"
          >
            基本信息
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 已选课程表格 -->
    <div v-if="showSelectedCoursesTable">
      <div class="selected-courses-header">
        <h3>已选课程列表</h3>
        <el-button type="primary" size="small" @click="backToSelection">返回选课</el-button>
      </div>
      <el-table
        :data="selectedCourses"
        border
        style="width: 100%"
        :header-cell-style="{background: '#f5f7fa', color: '#606266'}"
        v-loading="selectedLoading"
        row-height="100"
      >
        <el-table-column label="课程封面" align="center" width="120">
          <template slot-scope="scope">
            <div class="image-wrapper" @click="previewImage(scope.row.image)">
              <img
                v-if="scope.row.image"
                :src="formatImageUrl(scope.row.image)"
                class="course-cover"
                alt="课程封面"
                @error="handleImageError($event, scope.row)"
              >
              <div v-else class="no-image">暂无封面</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="course_name" label="课程名称" min-width="120" align="center" />
        <el-table-column prop="teacher_name" label="教师姓名" min-width="100" align="center" />
        <el-table-column prop="organization" label="所属单位" min-width="120" align="center" />
        <el-table-column prop="term" label="学期" min-width="100" align="center" />
        <el-table-column prop="profile" label="课程简介" min-width="180" align="center" />
        <el-table-column prop="target" label="课程目标" min-width="180" align="center" />
        <el-table-column label="操作" align="center" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="info"
              size="small"
              @click="viewCourseDetail(scope.row)"
              style="margin-right: 5px"
            >
              查看详情
            </el-button>
            <el-button
              type="danger"
              size="small"
              @click="dropCourse(scope.row)"
              :loading="dropLoading"
            >
              退选
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 图片预览对话框 -->
    <el-dialog :visible.sync="previewVisible" :append-to-body="true" class="img-preview-dialog">
      <img :src="previewImageUrl" alt="课程封面预览" class="preview-image">
    </el-dialog>

    <!-- 课程基本信息对话框 -->
    <el-dialog :visible.sync="basicInfoVisible" title="课程基本信息" width="600px" :append-to-body="true">
      <div v-if="selectedCourseInfo" class="course-basic-info">
        <div class="info-row">
          <div class="info-item">
            <label>课程名称：</label>
            <span>{{ selectedCourseInfo.course_name }}</span>
          </div>
          <div class="info-item">
            <label>教师姓名：</label>
            <span>{{ selectedCourseInfo.teacher_name }}</span>
          </div>
        </div>
        <div class="info-row">
          <div class="info-item">
            <label>所属单位：</label>
            <span>{{ selectedCourseInfo.organization || '未设置' }}</span>
          </div>
          <div class="info-item">
            <label>学期：</label>
            <span>{{ selectedCourseInfo.term || '未设置' }}</span>
          </div>
        </div>
        <div class="info-row">
          <div class="info-item full-width">
            <label>课程简介：</label>
            <span>{{ selectedCourseInfo.profile || '暂无简介' }}</span>
          </div>
        </div>
        <div class="info-row">
          <div class="info-item full-width">
            <label>课程目标：</label>
            <span>{{ selectedCourseInfo.target || '暂无目标' }}</span>
          </div>
        </div>
        <div class="info-row" v-if="selectedCourseInfo.image && selectedCourseInfo.image !== 'default'">
          <div class="info-item full-width">
            <label>课程封面：</label>
            <img :src="formatImageUrl(selectedCourseInfo.image)" class="course-cover-small" alt="课程封面" @error="handleImageError($event, selectedCourseInfo)">
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="basicInfoVisible = false">关闭</el-button>
        <el-button type="primary" @click="selectCourseFromBasicInfo">立即选课</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { getToken } from '@/utils/auth'
import { getTokenInfo } from '@/utils/jwtUtils'

export default {
  name: 'CourseSelection',
  data() {
    return {
      courseList: [],
      selectedCourses: [],
      loading: false,
      selectedLoading: false,
      selectLoading: false,
      dropLoading: false,
      showSelectedCoursesTable: false,
      defaultImage: require('@/assets/img/封面.png'),
      previewVisible: false,
      previewImageUrl: '',
      userId: '',
      // 筛选和搜索相关
      searchKeyword: '',
      teacherSearchKeyword: '',
      selectedOrganization: '',
      organizationOptions: [],
      // 课程基本信息对话框
      basicInfoVisible: false,
      selectedCourseInfo: null
    }
  },
  computed: {
    // 过滤后的课程列表
    filteredCourseList() {
      let filtered = this.courseList
      console.log('计算属性 - 原始课程列表长度:', this.courseList.length)

      // 按课程名称搜索
      if (this.searchKeyword && this.searchKeyword.trim()) {
        const keyword = this.searchKeyword.trim().toLowerCase()
        filtered = filtered.filter(course =>
          course.course_name && course.course_name.toLowerCase().includes(keyword)
        )
        console.log('课程名称搜索后课程列表长度:', filtered.length)
      }

      // 按教师姓名搜索
      if (this.teacherSearchKeyword && this.teacherSearchKeyword.trim()) {
        const teacherKeyword = this.teacherSearchKeyword.trim().toLowerCase()
        filtered = filtered.filter(course =>
          course.teacher_name && course.teacher_name.toLowerCase().includes(teacherKeyword)
        )
        console.log('教师姓名搜索后课程列表长度:', filtered.length)
      }

      // 按学校/单位筛选
      if (this.selectedOrganization) {
        filtered = filtered.filter(course =>
          course.organization === this.selectedOrganization
        )
        console.log('学校筛选后课程列表长度:', filtered.length)
      }

      console.log('最终过滤后的课程列表:', filtered)
      return filtered
    }
  },
  created() {
    this.getUserId()
    this.fetchAvailableCourses()
    this.fetchSelectedCourses()
  },
  methods: {
    getUserId() {
      try {
        const userInfo = getTokenInfo()
        console.log('获取用户ID时的用户信息:', userInfo) // 调试信息
        if (userInfo && userInfo.id) {
          this.userId = userInfo.id
          console.log('用户ID:', this.userId) // 调试信息
        } else {
          console.error('无法获取用户ID，用户信息:', userInfo)
          this.$message.error('无法获取用户信息，请重新登录')
        }
      } catch (error) {
        console.error('获取用户ID失败:', error)
        this.$message.error('无法获取用户信息，请重新登录')
      }
    },
    formatImageUrl(imageUrl) {
      const { getCourseCoverUrl } = require('@/utils/minio')
      const processedUrl = getCourseCoverUrl(imageUrl)
      return processedUrl || this.defaultImage
    },

    // 处理图片加载错误
    handleImageError(event, course) {
      console.warn('课程封面加载失败，已使用默认图片:', course.image)

      // 设置为默认图片
      event.target.src = this.defaultImage

      // 更新课程对象中的image字段，避免后续重复尝试加载
      if (course && course.image) {
        course.image = this.defaultImage
      }
    },

    // 预览图片
    previewImage(imageUrl) {
      if (!imageUrl) return

      this.previewImageUrl = this.formatImageUrl(imageUrl)
      this.previewVisible = true
    },

    // 获取可选课程列表
    async fetchAvailableCourses() {
      this.loading = true
      console.log('开始获取可选课程列表...')
      try {
        const response = await axios.get('/api/teachcourse/list', {
          headers: {
            'Authorization': 'Bearer ' + getToken()
          }
        })
        console.log('课程列表API响应:', response.data)

        // 显示所有课程（不再限制为科学课程）
        const allCourses = response.data || []
        console.log('所有课程:', allCourses)

        // 处理每个课程的图片地址
        this.courseList = allCourses.map(course => {
          // 保存原始图片URL，不直接修改course.image
          return {
            ...course,
            originalImage: course.image // 保存原始URL用于后续处理
          }
        })
        console.log('处理后的课程列表:', this.courseList)

        // 提取所有可用的学校/单位选项
        this.extractOrganizationOptions()
        console.log('学校/单位选项:', this.organizationOptions)
      } catch (error) {
        console.error('获取可选课程列表失败:', error)
        this.$message.error('获取可选课程列表失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    // 获取已选课程列表
    async fetchSelectedCourses() {
      if (!this.userId) {
        console.error('缺少用户ID，无法加载已选课程')
        this.$message.error('无法获取用户信息，请刷新页面重试')
        return
      }

      this.selectedLoading = true
      try {
        const response = await axios.get(`/api/user/course/list/${this.userId}`, {
          headers: {
            'Authorization': 'Bearer ' + getToken()
          }
        })
        if (response.data.code === 1) {
          const courses = response.data.data || []
          // 处理每个课程的图片地址
          this.selectedCourses = courses.map(course => {
            // 保存原始图片URL，不直接修改course.image
            return {
              ...course,
              originalImage: course.image // 保存原始URL用于后续处理
            }
          })
        } else {
          this.$message.error(response.data.msg || '获取已选课程列表失败')
        }
      } catch (error) {
        console.error('获取已选课程列表失败:', error)
        this.$message.error('获取已选课程列表失败，请稍后重试')
      } finally {
        this.selectedLoading = false
      }
    },

    // 选择课程
    async selectCourse(course) {
      if (!this.userId) {
        console.error('缺少用户ID，无法进行选课')
        this.$message.error('无法获取用户信息，请刷新页面重试')
        return
      }

      if (this.isSelected(course)) {
        this.$message.warning('您已经选择了该课程，不能重复选择')
        return
      }

      this.selectLoading = true
      try {
        if (!course.id) {
          this.$message.error('课程ID不存在，无法选课')
          return
        }

        if (!course.teacher_name) {
          this.$message.error('教师姓名不能为空')
          return
        }

        if (!course.course_name) {
          this.$message.error('课程名称不能为空')
          return
        }

        // 检查是否是AMC课程
        if (course.course_name === 'AMC') {
          // 获取用户信息
          const userInfo = getTokenInfo()
          console.log('用户信息:', userInfo) // 调试信息

          if (!userInfo) {
            this.$message.error('无法获取用户信息，请重新登录')
            return
          }

          // 检查用户名，可能字段名不同
          const username = userInfo.username || userInfo.name || userInfo.userName || userInfo.user_name
          if (!username) {
            console.error('用户信息中缺少用户名字段:', userInfo)
            this.$message.error('无法获取用户名，请重新登录')
            return
          }

          // AMC选课API已废弃，AMC信息表不再使用
          // 直接进行普通选课流程
          try {
            console.log('开始AMC选课流程')

            // 构建普通选课数据
            const selectCourse = {
              course_id: course.id,
              course_name: course.course_name,
              teacher_id: course.teacher_id || '',
              teacher_name: course.teacher_name,
              organization: course.organization || '',
              term: course.term || '',
              profile: course.profile || '',
              target: course.target || '',
              image: course.image || '',
              status: 1
            }

            // 调用普通选课API
            const response = await axios.post(`/api/user/course/select/${this.userId}`, selectCourse, {
              headers: {
                'Authorization': 'Bearer ' + getToken()
              }
            })

            if (response.data.code === 1) {
              this.$message.success('AMC选课成功')
              await Promise.all([
                this.fetchSelectedCourses(),
                this.fetchAvailableCourses()
              ])
            } else {
              this.$message.error(response.data.msg || '选课失败')
            }
          } catch (error) {
            console.error('AMC选课失败:', error)
            this.$message.error('选课失败，请稍后重试')
          }
          return
        }

        // 普通课程选课逻辑
        const selectCourse = {
          course_id: course.id,
          course_name: course.course_name,
          teacher_id: course.teacher_id || '',
          teacher_name: course.teacher_name,
          organization: course.organization || '',
          term: course.term || '',
          profile: course.profile || '',
          target: course.target || '',
          image: course.image || '',
          status: 1
        }
        const response = await axios.post(`/api/user/course/select/${this.userId}`, selectCourse, {
          headers: {
            'Authorization': 'Bearer ' + getToken()
          }
        })

        if (response.data.code === 1) {
          this.$message.success('选课成功')
          await Promise.all([
            this.fetchSelectedCourses(),
            this.fetchAvailableCourses()
          ])
        } else {
          this.$message.error(response.data.msg || '选课失败')
        }
      } catch (error) {
        console.error('选课失败:', error)
        if (error.response && error.response.data) {
          this.$message.error(error.response.data.msg || '选课失败，请稍后重试')
        } else {
          this.$message.error('选课失败，请稍后重试')
        }
      } finally {
        this.selectLoading = false
      }
    },

    // 退选课程
    async dropCourse(course) {
      this.dropLoading = true
      try {
        const courseId = course.id

        if (!courseId) {
          this.$message.error('选课记录ID不存在，无法退选')
          return
        }
        const response = await axios.delete(`/api/user/course/drop/${courseId}`, {
          headers: {
            'Authorization': 'Bearer ' + getToken()
          }
        })

        if (response.data.code === 1) {
          this.$message.success('退选成功')
          await Promise.all([
            this.fetchSelectedCourses(),
            this.fetchAvailableCourses()
          ])

          if (this.selectedCourses.length === 0) {
            setTimeout(() => {
              this.showSelectedCoursesTable = false
              this.$message.info('所有课程已退选，返回选课界面')
            }, 1000)
          }
        } else {
          this.$message.error(response.data.msg || '退选失败')
        }
      } catch (error) {
        console.error('退选失败:', error)
        if (error.response && error.response.data) {
          this.$message.error(error.response.data.msg || '退选失败，请稍后重试')
        } else {
          this.$message.error('退选失败，请稍后重试')
        }
      } finally {
        this.dropLoading = false
      }
    },

    isSelected(course) {
      return this.selectedCourses.some(item => {
        const isMatchedById = item.course_id === course.id || item.id === course.id
        const isMatchedByName = item.course_name === course.course_name &&
                               item.teacher_name === course.teacher_name
        return isMatchedById || isMatchedByName
      })
    },

    async finishSelection() {
      if (this.selectedCourses.length === 0) {
        this.$message.warning('您还没有选择任何课程')
        return
      }

      // 更新用户选课状态
      try {
        // 获取用户信息 - 使用与getUserId相同的方式
        const userInfo = getTokenInfo()
        if (!userInfo || !userInfo.id) {
          this.$message.error('无法获取用户信息，请重新登录')
          return
        }

        // 导入API函数
        const { updateUserMark } = await import('@/api/user')

        // 调用后端API更新mark字段
        const response = await updateUserMark(userInfo.id, 1)

        if (response.code === 1) {
          // 后端更新成功，同时更新前端状态
          const { updateUserStatus } = await import('@/utils/userStatus')
          updateUserStatus('mark', 1)

          this.$message.success('选课完成，即将跳转到我的课程界面')
          setTimeout(() => {
            this.$router.push('/course-management/index')
          }, 1500)
        } else {
          // 后端更新失败，只更新前端状态
          const { updateUserStatus } = await import('@/utils/userStatus')
          updateUserStatus('mark', 1)

          this.$message.success('选课完成，即将跳转到我的课程界面')
          setTimeout(() => {
            this.$router.push('/course-management/index')
          }, 1500)
        }
      } catch (error) {
        console.error('更新用户状态失败:', error)
        // 出错时也更新前端状态
        const { updateUserStatus } = await import('@/utils/userStatus')
        updateUserStatus('mark', 1)

        this.$message.success('选课完成，即将跳转到我的课程界面')
        setTimeout(() => {
          this.$router.push('/course-management/index')
        }, 1500)
      }
    },

    viewSelectedCourses() {
      this.showSelectedCoursesTable = true
    },

    backToSelection() {
      this.showSelectedCoursesTable = false
    },

    // 提取所有可用的学校/单位选项
    extractOrganizationOptions() {
      const organizations = new Set()
      this.courseList.forEach(course => {
        if (course.organization && course.organization.trim()) {
          organizations.add(course.organization.trim())
        }
      })
      this.organizationOptions = Array.from(organizations).sort()
    },

    // 处理课程名称搜索
    handleSearch() {
      // 搜索逻辑在computed属性中处理，这里可以添加防抖等优化
    },

    // 处理教师姓名搜索
    handleTeacherSearch() {
      // 搜索逻辑在computed属性中处理，这里可以添加防抖等优化
    },

    // 处理筛选
    handleFilter() {
      // 筛选逻辑在computed属性中处理
    },

    // 重置筛选条件
    resetFilters() {
      this.searchKeyword = ''
      this.teacherSearchKeyword = ''
      this.selectedOrganization = ''
    },

    // 查看课程详情（已选课程）
    viewCourseDetail(course) {
      // 跳转到课程详情页面，使用course_id（课程ID）而不是id（选课记录ID）
      const courseId = course.course_id || course.id
      // 学生端在新页面打开课程详情
      const courseDetailUrl = `${window.location.origin}/#/course/detail/${courseId}`
      console.log('学生端，在新页面打开课程详情:', courseDetailUrl)
      window.open(courseDetailUrl, '_blank')
    },

    // 查看课程基本信息（未选课程）
    viewCourseBasicInfo(course) {
      this.selectedCourseInfo = course
      this.basicInfoVisible = true
    },

    // 从基本信息对话框选课
    selectCourseFromBasicInfo() {
      if (this.selectedCourseInfo) {
        this.basicInfoVisible = false
        this.selectCourse(this.selectedCourseInfo)
      }
    }
  }
}
</script>

<style scoped>
.app-container {
  padding: 20px;
  min-height: calc(100vh - 84px);
  background-color: #f0f2f5;
}

.selection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.selection-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: #303133;
}

.filter-section {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.selected-courses-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.selected-courses-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: #303133;
}

/* 课程封面样式 */
.image-wrapper {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border-radius: 4px;
  width: 80px;
  height: 80px;
  margin: 0 auto;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.image-wrapper::before {
  content: '点击查看';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
}

.image-wrapper:hover::before {
  opacity: 1;
}

.image-wrapper:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 2;
}

.course-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
  color: #909399;
  font-size: 12px;
  border-radius: 4px;
  border: 1px dashed #d9d9d9;
}

/* 图片预览对话框样式 */
.img-preview-dialog /deep/ .el-dialog {
  text-align: center;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.img-preview-dialog /deep/ .el-dialog__body {
  padding: 0;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

/* 表格行样式 */
.el-table /deep/ .el-table__row {
  height: 100px;
}

.el-table /deep/ .el-table__cell {
  padding: 10px 0;
}

/* 适配Element UI的表格样式 */
.el-table {
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

/* 课程基本信息对话框样式 */
.course-basic-info {
  padding: 20px 0;
}

.info-row {
  display: flex;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.info-item {
  flex: 1;
  min-width: 200px;
  margin-right: 20px;
  margin-bottom: 10px;
}

.info-item.full-width {
  flex: 100%;
  margin-right: 0;
}

.info-item label {
  font-weight: 600;
  color: #606266;
  margin-right: 8px;
  display: inline-block;
  min-width: 80px;
}

.info-item span {
  color: #303133;
  word-break: break-all;
}

.course-cover-small {
  width: 120px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  margin-top: 5px;
}

.dialog-footer {
  text-align: right;
}
</style>
